/**
 * glTF textures arrive as decoded ImageBitmaps, and three keeps them alive after the upload (texture.image): 50 of them
 * held 142 MB of decoded pixels next to the same pixels on the GPU (measured on the phone class, 2026-10-08). The
 * upload is a copy, so the bitmap is closed the moment three has uploaded and mipmapped the texture. Pixel for pixel
 * nothing changes on screen; what needs the size later (the cut-out shaders) reads `userData.size`.
 */
import * as THREE from 'three/webgpu';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

const isBitmap = (x: unknown): x is ImageBitmap => typeof ImageBitmap !== 'undefined' && x instanceof ImageBitmap;

/** Width and height of a texture's image, also after its bitmap was released (1024 when unknown). */
export function textureSize(t: THREE.Texture): [number, number] {
  const kept = t.userData.size as [number, number] | undefined;
  if (kept) return kept;
  const img = t.image as { width?: number; height?: number } | undefined;
  return [img?.width ?? 1024, img?.height ?? 1024];
}

/** Closes `img` once every texture in `users` was uploaded: GLTFLoader makes one Texture per use of an image, uploaded apart. */
function closeAfterUpload(img: ImageBitmap, users: THREE.Texture[]): void {
  let pending = users.length;
  for (const t of users) {
    t.userData.size = [img.width, img.height];
    const prev = t.onUpdate;
    t.onUpdate = (tex) => {
      prev?.(tex);
      tex.onUpdate = prev;
      if (--pending === 0 && users.every((u) => u.image === img)) img.close();
    };
  }
}

/** Whether decoded bitmaps are released after the upload: on every backend, unless the page has ?bitmaps=keep (QA). */
export function releaseBitmaps(): boolean {
  return !/[?&]bitmaps=keep/.test(location.search);
}

/**
 * Releases every bitmap texture of a loaded glTF after its upload; returns the glTF. A bitmap shared by several textures is
 * closed after the last of them was uploaded: closing it after the first broke the second upload on WebGL2 (WebKit, 2026-10-13).
 */
export function releaseAfterUpload<T extends Pick<GLTF, 'scene'>>(g: T, enabled = true): T {
  if (!enabled) return g;
  const users = new Map<ImageBitmap, Set<THREE.Texture>>();
  g.scene.traverse((o) => {
    for (const m of [(o as THREE.Mesh).material].flat()) {
      if (!m) continue;
      for (const v of Object.values(m)) {
        if (!(v instanceof THREE.Texture) || !isBitmap(v.image) || v.userData.size) continue;
        const set = users.get(v.image) ?? new Set();
        users.set(v.image, set.add(v));
      }
    }
  });
  for (const [img, set] of users) closeAfterUpload(img, [...set]);
  return g;
}
